import React, { useState, useEffect, useCallback, useRef } from "react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Building2, MapPin, Package, Users, Tags,
  Plus, Trash2, Eye, EyeOff, Check, X, Save, Upload,
  Phone, Mail, Clock, Shield, User, BadgeCheck, Pencil,
  Star, ChevronRight, LayoutList, Search, Navigation, Layers,
} from "lucide-react";
import { getImageUrl } from "../../utils/getImageUrl";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// ─── Shared styles ─────────────────────────────────────────────────────────────
const inp =
  "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 bg-white";
const btn = "px-4 py-2 rounded-xl text-sm font-semibold transition inline-flex items-center gap-2";

// ─── Modal wrapper ──────────────────────────────────────────────────────────────
function Modal({ open, onClose, title, children, size = "md" }) {
  if (!open) return null;
  const widths = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-4xl" };
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className={`bg-white rounded-2xl shadow-2xl w-full ${widths[size]} max-h-[90vh] overflow-y-auto`}
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
            <h3 className="font-bold text-gray-800 text-base">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition">
              <X size={18} />
            </button>
          </div>
          <div className="p-6">{children}</div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── Empty state ────────────────────────────────────────────────────────────────
function Empty({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-gray-300">
      <Icon size={44} className="mb-3 opacity-40" />
      <p className="text-sm text-gray-400">{label}</p>
    </div>
  );
}

// ─── INFO TAB ───────────────────────────────────────────────────────────────────
function InfoTab({ company, categories, onRefresh }) {
  const [form, setForm] = useState({
    name: company.name || "",
    slug: company.slug || "",
    description: company.description || "",
    categoryId: company.category?.id || company.categoryId || "",
  });
  const [logo, setLogo] = useState(null);
  const [previewLogo, setPreviewLogo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState(company.plan?.id || company.planId || "");
  const [savingPlan, setSavingPlan] = useState(false);

  useEffect(() => {
    API.get("/memberships/plans").then((res) => setPlans(res.data?.data?.plans ?? [])).catch(() => {});
  }, []);
  useEffect(() => { setPlanId(company.plan?.id || company.planId || ""); }, [company.id, company.plan?.id, company.planId]);

  const handleSavePlan = async () => {
    if (!planId) { toast.warn("Selecciona un plan"); return; }
    setSavingPlan(true);
    try {
      await API.put(`/memberships/companies/${company.id}/plan`, { planId });
      toast.success("Plan de membresía actualizado");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.message || "Error al asignar el plan"); }
    finally { setSavingPlan(false); }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogo(file);
    setPreviewLogo(URL.createObjectURL(file));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug) { toast.warn("Nombre y slug son requeridos"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("slug", form.slug);
      fd.append("description", form.description);
      fd.append("category", form.categoryId);
      if (logo instanceof File) fd.append("logo", logo);
      await API.put(`/companies/${company.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Empresa actualizada correctamente");
      setLogo(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async () => {
    try {
      const newStatus = company.status === "Activo" ? "Inactivo" : "Activo";
      await API.patch(`/companies/${company.id}/status`, { status: newStatus });
      toast.success(`Empresa ${newStatus === "Activo" ? "activada" : "desactivada"}`);
      onRefresh();
    } catch { toast.error("Error cambiando estado"); }
  };

  const toggleVerified = async () => {
    try {
      await API.patch(`/companies/${company.id}/status`, { verified: !company.verified });
      toast.success(company.verified ? "Verificación removida" : "Empresa verificada");
      onRefresh();
    } catch { toast.error("Error cambiando verificación"); }
  };

  const logoSrc = previewLogo || getImageUrl(company.logo);

  return (
    <form onSubmit={handleSave} className="space-y-6">
      {/* Logo upload */}
      <div className="flex items-center gap-5">
        <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200 shrink-0">
          {logoSrc
            ? <img src={logoSrc} alt="Logo" className="w-full h-full object-cover" />
            : <Building2 size={28} className="text-gray-400" />
          }
        </div>
        <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 hover:border-supernova-400 text-sm text-gray-600 transition">
          <Upload size={14} />
          {logo ? "Imagen seleccionada" : "Cambiar logo"}
          <input type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
        </label>
      </div>

      {/* Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Nombre *</label>
          <input className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nombre de la empresa" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Slug *</label>
          <input className={inp} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="slug-url" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Descripción</label>
          <textarea
            className={`${inp} min-h-[90px] resize-none`}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Describe la empresa..."
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Categoría</label>
          <select className={inp} value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
            <option value="">Sin categoría</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      {/* Status toggles */}
      <div className="flex flex-wrap gap-3 pt-2">
        <button type="button" onClick={toggleStatus}
          className={`${btn} border ${company.status === "Activo"
            ? "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
            : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"}`}>
          {company.status === "Activo" ? <Check size={14} /> : <X size={14} />}
          {company.status === "Activo" ? "Activa" : "Inactiva"}
        </button>
        <button type="button" onClick={toggleVerified}
          className={`${btn} border ${company.verified
            ? "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
            : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"}`}>
          <BadgeCheck size={14} />
          {company.verified ? "Verificada" : "Sin verificar"}
        </button>
      </div>

      {/* Owner */}
      {company.owner && (
        <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Propietario</p>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-supernova-100 rounded-full flex items-center justify-center">
              <User size={15} className="text-supernova-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">{company.owner.name}</p>
              <p className="text-xs text-gray-500">{company.owner.email}</p>
            </div>
            <span className="ml-auto text-xs bg-supernova-50 text-supernova-700 border border-supernova-200 px-2 py-0.5 rounded-full font-semibold">
              Admin asignado
            </span>
          </div>
        </div>
      )}

      {/* Plan de membresía — % de comisión que se le cobra a esta tienda por pedido */}
      <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Plan de membresía</p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <select className={`${inp} sm:max-w-xs`} value={planId} onChange={(e) => setPlanId(e.target.value)}>
            <option value="">Sin plan asignado</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>{p.name} — {Number(p.commissionPercent)}% comisión</option>
            ))}
          </select>
          <button type="button" onClick={handleSavePlan} disabled={savingPlan}
            className={`${btn} bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-60 shrink-0`}>
            <Save size={13} /> {savingPlan ? "Guardando..." : "Asignar plan"}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          El % de comisión se aplica a los pedidos nuevos desde que se asigna — no cambia pedidos ya creados.
        </p>
      </div>

      <div className="pt-2">
        <button type="submit" disabled={saving}
          className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 disabled:opacity-60`}>
          <Save size={14} />
          {saving ? "Guardando..." : "Guardar cambios"}
        </button>
      </div>
    </form>
  );
}

// ─── SEDES TAB ──────────────────────────────────────────────────────────────────
// ─── Leaflet icon fix ──────────────────────────────────────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:       "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:     "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

function LocationPicker({ location, setLocation }) {
  useMapEvents({ click(e) { setLocation({ lat: e.latlng.lat, lng: e.latlng.lng }); } });
  return location.lat && location.lng
    ? <Marker position={[parseFloat(location.lat), parseFloat(location.lng)]} />
    : null;
}

function MapFlyTo({ flyTo }) {
  const map = useMap();
  useEffect(() => { if (flyTo) map.flyTo(flyTo, 16, { duration: 1 }); }, [flyTo, map]);
  return null;
}

// ─── SEDES TAB ──────────────────────────────────────────────────────────────────
const DAYS = [
  { value: "monday",    label: "Lunes" },
  { value: "tuesday",   label: "Martes" },
  { value: "wednesday", label: "Miércoles" },
  { value: "thursday",  label: "Jueves" },
  { value: "friday",    label: "Viernes" },
  { value: "saturday",  label: "Sábado" },
  { value: "sunday",    label: "Domingo" },
];

const DAY_LABELS = Object.fromEntries(DAYS.map(({ value, label }) => [value, label]));

const DEFAULT_SCHEDULE = DAYS.map(({ value }) => ({ day: value, open: "08:00", close: "18:00", closed: false }));

function ScheduleEditor({ schedule, onChange }) {
  const arr = schedule?.length === 7 ? schedule : DEFAULT_SCHEDULE;
  return (
    <div className="space-y-2">
      {arr.map((s, idx) => (
        <div key={s.day} className="flex items-center gap-3 text-sm">
          <span className="w-20 shrink-0 text-xs font-medium text-gray-600">{DAY_LABELS[s.day] ?? s.day}</span>
          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={!s.closed}
              onChange={(e) => {
                const next = [...arr];
                next[idx] = { ...s, closed: !e.target.checked };
                onChange(next);
              }}
            />
            Abierto
          </label>
          {!s.closed ? (
            <>
              <input type="time" value={s.open}
                className="border border-gray-200 rounded-lg px-2 py-1 text-xs"
                onChange={(e) => { const n = [...arr]; n[idx] = { ...s, open: e.target.value }; onChange(n); }} />
              <span className="text-gray-400 text-xs">—</span>
              <input type="time" value={s.close}
                className="border border-gray-200 rounded-lg px-2 py-1 text-xs"
                onChange={(e) => { const n = [...arr]; n[idx] = { ...s, close: e.target.value }; onChange(n); }} />
            </>
          ) : (
            <span className="text-xs text-gray-400 italic">Cerrado</span>
          )}
        </div>
      ))}
    </div>
  );
}

function SedeForm({ form, setForm }) {
  const [searchQuery,   setSearchQuery]   = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching,     setSearching]     = useState(false);
  const [flyTo,         setFlyTo]         = useState(null);
  const [gpsLoading,    setGpsLoading]    = useState(false);
  const [satellite,     setSatellite]     = useState(false);
  const searchTimer = useRef(null);

  const location = { lat: form.lat || "", lng: form.lng || "" };
  const setLocation = ({ lat, lng }) => setForm((prev) => ({ ...prev, lat, lng }));

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    setSearchResults([]);
    clearTimeout(searchTimer.current);
    if (q.length < 3) return;
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5`,
          { headers: { "Accept-Language": navigator.language || "es" } }
        );
        setSearchResults(await res.json());
      } catch { /* silently ignore */ }
      finally { setSearching(false); }
    }, 600);
  };

  const handleSelectResult = (r) => {
    const lat = parseFloat(r.lat);
    const lng = parseFloat(r.lon);
    setForm((prev) => ({ ...prev, lat, lng, address: prev.address || r.display_name }));
    setFlyTo([lat, lng]);
    setSearchQuery(r.display_name);
    setSearchResults([]);
  };

  const handleGPS = () => {
    if (!navigator.geolocation) { toast.warn("GPS no disponible en este navegador"); return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const lat = coords.latitude;
        const lng = coords.longitude;
        setForm((prev) => ({ ...prev, lat, lng }));
        setFlyTo([lat, lng]);
        setGpsLoading(false);
      },
      () => { toast.error("No se pudo obtener la ubicación"); setGpsLoading(false); }
    );
  };

  const mapCenter = location.lat && location.lng
    ? [parseFloat(location.lat), parseFloat(location.lng)]
    : [4.6097, -74.0817];

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Nombre *</label>
          <input className={inp} value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Dirección</label>
          <input className={inp} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Teléfono</label>
          <input className={inp} value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Email</label>
          <input className={inp} type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
      </div>

      {/* Mapa de ubicación */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Ubicación en el mapa</p>
        <div className="relative mb-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                className={`${inp} pl-8`}
                placeholder="Buscar dirección en el mapa..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              {searching && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 animate-pulse">buscando…</span>}
            </div>
            <button
              type="button"
              onClick={handleGPS}
              disabled={gpsLoading}
              title="Usar mi ubicación actual"
              className="px-3 py-2 rounded-xl border border-gray-200 hover:border-supernova-400 text-gray-500 hover:text-supernova-600 transition disabled:opacity-50"
            >
              <Navigation size={15} className={gpsLoading ? "animate-spin" : ""} />
            </button>
          </div>
          {searchResults.length > 0 && (
            <ul className="absolute z-[1000] w-full bg-white border border-gray-200 rounded-xl shadow-lg mt-1 max-h-40 overflow-y-auto text-sm">
              {searchResults.map((r) => (
                <li
                  key={r.place_id}
                  className="px-3 py-2 hover:bg-supernova-50 cursor-pointer text-gray-700 truncate border-b border-gray-50 last:border-0"
                  onClick={() => handleSelectResult(r)}
                >
                  {r.display_name}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="relative rounded-xl overflow-hidden border border-gray-200" style={{ height: 280 }}>
          <MapContainer center={mapCenter} zoom={14} style={{ height: "100%", width: "100%" }}>
            {satellite ? (
              <TileLayer
                key="satellite"
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution="© Esri, Maxar, GeoEye, Earthstar Geographics"
                maxZoom={19}
              />
            ) : (
              <TileLayer
                key="street"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="© OpenStreetMap contributors"
              />
            )}
            <LocationPicker location={location} setLocation={setLocation} />
            <MapFlyTo flyTo={flyTo} />
          </MapContainer>
          <button
            type="button"
            onClick={() => setSatellite(s => !s)}
            title={satellite ? "Vista de mapa" : "Vista satélite"}
            style={{ position: "absolute", bottom: 10, right: 10, zIndex: 1000 }}
            className="flex items-center gap-1.5 bg-white border border-gray-200 shadow px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50 transition"
          >
            <Layers size={13} />
            {satellite ? "Mapa" : "Satélite"}
          </button>
        </div>
        {location.lat && location.lng ? (
          <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
            <MapPin size={10} />
            {parseFloat(location.lat).toFixed(6)}, {parseFloat(location.lng).toFixed(6)}
          </p>
        ) : (
          <p className="text-xs text-gray-400 mt-1.5">Haz clic en el mapa para seleccionar la ubicación</p>
        )}
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Redes sociales</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { key: "whatsapp", label: "WhatsApp" },
            { key: "instagram", label: "Instagram" },
            { key: "facebook", label: "Facebook" },
            { key: "web", label: "Sitio web" },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="text-xs text-gray-500 mb-1 block">{label}</label>
              <input className={inp} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder="https://..." />
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Horarios de atención</p>
        <ScheduleEditor
          schedule={form.schedule}
          onChange={(sched) => setForm({ ...form, schedule: sched })}
        />
      </div>
    </div>
  );
}

function SedesTab({ company, sedes, setSedes }) {
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setForm({ schedule: DEFAULT_SCHEDULE }); setModal("create"); };
  const openEdit = (s) => { setForm({ ...s, schedule: s.schedules?.length ? s.schedules : DEFAULT_SCHEDULE }); setModal(s.id); };
  const closeModal = () => { setModal(null); setForm({}); };

  const fetchSedes = async () => {
    const res = await API.get(`/sedes/company/${company.id}`);
    setSedes(res.data?.data?.sedes ?? []);
  };

  const handleSave = async () => {
    if (!form.name) { toast.warn("El nombre es requerido"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        address: form.address || "",
        phone: form.phone || "",
        email: form.email || "",
        lat: parseFloat(form.lat) || 0,
        lng: parseFloat(form.lng) || 0,
        whatsapp: form.whatsapp || "",
        instagram: form.instagram || "",
        facebook: form.facebook || "",
        web: form.web || "",
        schedule: form.schedule ? JSON.stringify(form.schedule) : undefined,
      };
      if (modal === "create") {
        await API.post(`/sedes/company/${company.id}`, payload);
        toast.success("Sede creada");
      } else {
        await API.put(`/sedes/${modal}`, payload);
        toast.success("Sede actualizada");
      }
      await fetchSedes();
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al guardar sede");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta sede permanentemente?")) return;
    try {
      await API.delete(`/sedes/${id}`);
      setSedes((prev) => prev.filter((s) => s.id !== id));
      toast.success("Sede eliminada");
    } catch { toast.error("Error al eliminar sede"); }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-gray-500">{sedes.length} sede{sedes.length !== 1 ? "s" : ""} registrada{sedes.length !== 1 ? "s" : ""}</p>
        <button onClick={openCreate} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600`}>
          <Plus size={14} /> Nueva sede
        </button>
      </div>

      {sedes.length === 0 ? (
        <Empty icon={MapPin} label="No hay sedes registradas" />
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {sedes.map((s) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 rounded-2xl p-4 border border-gray-100 hover:border-gray-200 transition"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="min-w-0">
                  <p className="font-bold text-gray-800 text-sm">{s.name}</p>
                  {s.address && (
                    <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                      <MapPin size={10} /> {s.address}
                    </p>
                  )}
                </div>
                <div className="flex gap-1.5 shrink-0 ml-2">
                  <button onClick={() => openEdit(s)}
                    className="p-1.5 rounded-lg bg-white border border-gray-200 hover:border-supernova-400 text-gray-500 hover:text-supernova-600 transition">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => handleDelete(s.id)}
                    className="p-1.5 rounded-lg bg-white border border-gray-200 hover:border-red-300 text-gray-500 hover:text-red-500 transition">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {s.phone && (
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <Phone size={10} /> {s.phone}
                  </span>
                )}
                {s.email && (
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <Mail size={10} /> {s.email}
                  </span>
                )}
              </div>
              {s.schedules?.length > 0 && (
                <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                  <Clock size={10} />
                  {s.schedules.filter((sc) => !sc.closed).length} días activos
                </p>
              )}
            </motion.div>
          ))}
        </div>
      )}

      <Modal open={modal !== null} onClose={closeModal} title={modal === "create" ? "Nueva sede" : "Editar sede"} size="xl">
        <SedeForm form={form} setForm={setForm} />
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button onClick={closeModal} className={`${btn} border border-gray-200 text-gray-600 hover:bg-gray-50`}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 disabled:opacity-60`}>
            <Save size={14} /> {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </Modal>
    </>
  );
}

// ─── PRODUCTOS TAB ──────────────────────────────────────────────────────────────
function ProductoForm({ form, setForm, secciones, sedes }) {
  const [newPreviews, setNewPreviews] = useState([]);

  const handleImages = (e) => {
    const files = Array.from(e.target.files);
    setForm({ ...form, imageFiles: files });
    setNewPreviews(files.map((f) => URL.createObjectURL(f)));
  };

  const existingImages = Array.isArray(form.images) ? form.images : (form.images ? [form.images] : []);

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Título *</label>
          <input className={inp} value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Nombre del producto" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Descripción</label>
          <textarea className={`${inp} min-h-[70px] resize-none`} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descripción del producto..." />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Precio (COP) *</label>
          <input className={inp} type="number" min="0" step="100" value={form.price || ""} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="15000" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Precio oferta</label>
          <input className={inp} type="number" min="0" step="100" value={form.offerPrice || ""} onChange={(e) => setForm({ ...form, offerPrice: e.target.value })} placeholder="Opcional" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Stock</label>
          <input className={inp} type="number" min="0" value={form.stock || ""} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="0 = ilimitado" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Sección</label>
          <select className={inp} value={form.seccionId || ""} onChange={(e) => setForm({ ...form, seccionId: e.target.value })}>
            <option value="">Sin sección</option>
            {secciones.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-gray-500 mb-2 block uppercase tracking-wider">Sedes *</label>
          <div className="grid grid-cols-2 gap-2 max-h-36 overflow-y-auto border border-gray-200 rounded-xl p-3">
            {sedes?.length > 0 ? sedes.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  value={s.id}
                  checked={(form.selectedSedes ?? []).includes(s.id)}
                  onChange={(e) => {
                    const curr = form.selectedSedes ?? [];
                    setForm({ ...form, selectedSedes: e.target.checked ? [...curr, s.id] : curr.filter((i) => i !== s.id) });
                  }}
                  className="accent-supernova-500"
                />
                {s.name}
              </label>
            )) : (
              <p className="text-xs text-gray-400 col-span-2">No hay sedes registradas</p>
            )}
          </div>
        </div>
        <div className="sm:col-span-2 flex items-center gap-2">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <div className={`w-10 h-5 rounded-full transition relative ${form.visible !== false ? "bg-supernova-500" : "bg-gray-300"}`}
              onClick={() => setForm({ ...form, visible: form.visible !== false ? false : true })}>
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.visible !== false ? "translate-x-5" : "translate-x-0.5"}`} />
            </div>
            <span className="text-sm text-gray-600 font-medium">Visible al público</span>
          </label>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-gray-500 mb-2 block uppercase tracking-wider">Imágenes</label>
          <label className="cursor-pointer flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-gray-200 hover:border-supernova-400 text-sm text-gray-500 transition">
            <Upload size={16} className="text-gray-400" />
            {form.imageFiles?.length ? `${form.imageFiles.length} imagen(es) seleccionada(s)` : "Seleccionar imágenes"}
            <input type="file" accept="image/*" multiple className="hidden" onChange={handleImages} />
          </label>
          {newPreviews.length > 0 && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {newPreviews.map((src, i) => (
                <img key={i} src={src} alt="" className="w-16 h-16 rounded-xl object-cover border border-gray-200 shadow-sm" />
              ))}
            </div>
          )}
          {!newPreviews.length && existingImages.length > 0 && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {existingImages.slice(0, 5).map((src, i) => (
                <img key={i} src={getImageUrl(src)} alt="" className="w-16 h-16 rounded-xl object-cover border border-gray-200 shadow-sm" />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductosTab({ company, products, setProducts, secciones, sedes }) {
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const openCreate = () => { setForm({ visible: true, selectedSedes: [] }); setModal("create"); };
  const openEdit = (p) => { setForm({ ...p, selectedSedes: p.sedes?.map((s) => s.id) ?? [] }); setModal(p.id); };
  const closeModal = () => { setModal(null); setForm({}); };

  const fetchProducts = async () => {
    const res = await API.get(`/products/company/${company.id}`);
    setProducts(res.data?.data?.products ?? res.data?.data ?? []);
  };

  const handleSave = async () => {
    if (!form.title) { toast.warn("El título es requerido"); return; }
    if (!form.price) { toast.warn("El precio es requerido"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description || "");
      fd.append("price", form.price);
      if (form.offerPrice) fd.append("offerPrice", form.offerPrice);
      else fd.append("offerPrice", "");
      if (form.stock) fd.append("stock", form.stock);
      fd.append("visible", form.visible !== false ? "true" : "false");
      if (form.seccionId) fd.append("seccionId", form.seccionId);
      if (form.selectedSedes?.length) form.selectedSedes.forEach((id) => fd.append("sedes", id));
      if (form.imageFiles?.length) form.imageFiles.forEach((f) => fd.append("images", f));
      if (modal === "create") {
        fd.append("companyId", company.id);
        await API.post("/products", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Producto creado");
      } else {
        await API.put(`/products/${modal}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Producto actualizado");
      }
      await fetchProducts();
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al guardar producto");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este producto? Esta acción no se puede deshacer.")) return;
    try {
      await API.delete(`/products/${id}`);
      setProducts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Producto eliminado");
    } catch { toast.error("Error al eliminar producto"); }
  };

  const toggleVisible = async (p) => {
    try {
      const fd = new FormData();
      fd.append("visible", p.visible ? "false" : "true");
      await API.put(`/products/${p.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, visible: !x.visible } : x));
      toast.success(p.visible ? "Producto ocultado" : "Producto visible");
    } catch { toast.error("Error al cambiar visibilidad"); }
  };

  const filtered = products.filter((p) =>
    !search || p.title?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
        <input
          className={`${inp} max-w-xs`}
          placeholder="Buscar producto..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button onClick={openCreate} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 shrink-0`}>
          <Plus size={14} /> Nuevo producto
        </button>
      </div>

      {filtered.length === 0 ? (
        <Empty icon={Package} label={search ? "Sin resultados" : "No hay productos registrados"} />
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => {
            const img = Array.isArray(p.images) ? p.images[0] : (typeof p.images === "string" ? p.images : null);
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-3 border border-gray-100 hover:border-gray-200 transition"
              >
                {/* Thumbnail */}
                <div className="w-12 h-12 rounded-xl bg-gray-200 overflow-hidden shrink-0 flex items-center justify-center">
                  {img
                    ? <img src={getImageUrl(img)} alt={p.title} className="w-full h-full object-cover" />
                    : <Package size={18} className="text-gray-400" />
                  }
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-800 truncate">{p.title}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-xs font-bold text-supernova-600">
                      ${Number(p.price).toLocaleString("es-CO")}
                    </span>
                    {p.offerPrice && (
                      <span className="text-xs text-gray-400 line-through">
                        ${Number(p.offerPrice).toLocaleString("es-CO")}
                      </span>
                    )}
                    {p.seccion && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">
                        {p.seccion.name}
                      </span>
                    )}
                  </div>
                </div>
                {/* Actions */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => toggleVisible(p)}
                    title={p.visible ? "Ocultar" : "Mostrar"}
                    className={`p-1.5 rounded-lg border transition ${p.visible
                      ? "bg-green-50 border-green-200 text-green-600 hover:bg-green-100"
                      : "bg-gray-100 border-gray-200 text-gray-400 hover:bg-gray-200"}`}>
                    {p.visible ? <Eye size={13} /> : <EyeOff size={13} />}
                  </button>
                  <button onClick={() => openEdit(p)}
                    className="p-1.5 rounded-lg bg-white border border-gray-200 hover:border-supernova-400 text-gray-500 hover:text-supernova-600 transition">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => handleDelete(p.id)}
                    className="p-1.5 rounded-lg bg-white border border-gray-200 hover:border-red-300 text-gray-500 hover:text-red-500 transition">
                    <Trash2 size={13} />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <Modal open={modal !== null} onClose={closeModal} title={modal === "create" ? "Nuevo producto" : "Editar producto"} size="lg">
        <ProductoForm form={form} setForm={setForm} secciones={secciones} sedes={sedes} />
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button onClick={closeModal} className={`${btn} border border-gray-200 text-gray-600 hover:bg-gray-50`}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 disabled:opacity-60`}>
            <Save size={14} /> {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </Modal>
    </>
  );
}

// ─── SECCIONES TAB ──────────────────────────────────────────────────────────────
function SeccionesTab({ company, secciones, setSecciones }) {
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const res = await API.post("/secciones", { name: newName.trim(), companyId: company.id });
      const sec = res.data?.data?.seccion ?? {};
      setSecciones((prev) => [...prev, sec]);
      setNewName("");
      toast.success("Sección creada");
    } catch { toast.error("Error al crear sección"); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try {
      await API.put(`/secciones/${id}`, { name: editName.trim() });
      setSecciones((prev) => prev.map((s) => s.id === id ? { ...s, name: editName.trim() } : s));
      setEditId(null);
      toast.success("Sección actualizada");
    } catch { toast.error("Error al actualizar sección"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta sección?")) return;
    try {
      await API.delete(`/secciones/${id}`);
      setSecciones((prev) => prev.filter((s) => s.id !== id));
      toast.success("Sección eliminada");
    } catch { toast.error("Error al eliminar sección"); }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleCreate} className="flex gap-2">
        <input
          className={`${inp} flex-1`}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Nueva sección (ej: Hamburguesas, Bebidas, Postres...)"
        />
        <button type="submit" disabled={saving || !newName.trim()} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 disabled:opacity-50 shrink-0`}>
          <Plus size={14} /> Agregar
        </button>
      </form>

      {secciones.length === 0 ? (
        <Empty icon={LayoutList} label="No hay secciones creadas" />
      ) : (
        <div className="space-y-2">
          {secciones.map((s) => (
            <div key={s.id} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
              {editId === s.id ? (
                <>
                  <input
                    className={`${inp} flex-1`}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => { if (e.key === "Enter") handleUpdate(s.id); if (e.key === "Escape") setEditId(null); }}
                  />
                  <button onClick={() => handleUpdate(s.id)} className={`${btn} bg-green-500 text-white hover:bg-green-600`}><Check size={14} /></button>
                  <button onClick={() => setEditId(null)} className={`${btn} border border-gray-200 text-gray-500 hover:bg-gray-100`}><X size={14} /></button>
                </>
              ) : (
                <>
                  <Tags size={15} className="text-gray-400 shrink-0" />
                  <span className="flex-1 text-sm font-medium text-gray-700">{s.name}</span>
                  <span className="text-xs text-gray-400">{s.products?.length ?? 0} productos</span>
                  <button onClick={() => { setEditId(s.id); setEditName(s.name); }}
                    className="p-1.5 rounded-lg bg-white border border-gray-200 hover:border-supernova-400 text-gray-500 hover:text-supernova-600 transition">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => handleDelete(s.id)}
                    className="p-1.5 rounded-lg bg-white border border-gray-200 hover:border-red-300 text-gray-500 hover:text-red-500 transition">
                    <Trash2 size={13} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── USUARIOS TAB ───────────────────────────────────────────────────────────────
const ROLE_META = {
  company: { label: "Admin empresa",  cls: "bg-blue-50 text-blue-700 border-blue-200" },
  user:    { label: "Empleado",        cls: "bg-gray-100 text-gray-600 border-gray-200" },
  admin:   { label: "Súper admin",     cls: "bg-red-50 text-red-600 border-red-200" },
  ryder:   { label: "Ryder",           cls: "bg-orange-50 text-orange-600 border-orange-200" },
};

function UsuariosTab({ company, users, setUsers }) {
  const [createModal, setCreateModal] = useState(false);
  const [resetModal, setResetModal] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchUsers = async () => {
    const res = await API.get(`/companies/${company.id}/users`);
    setUsers(res.data?.data?.users ?? res.data?.data?.data ?? []);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.warn("Completa todos los campos"); return; }
    if (form.password.length < 6) { toast.warn("La contraseña debe tener al menos 6 caracteres"); return; }
    setSaving(true);
    try {
      await API.post(`/companies/${company.id}/users`, form);
      toast.success("Usuario creado con éxito");
      await fetchUsers();
      setCreateModal(false);
      setForm({ name: "", email: "", password: "", role: "user" });
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al crear usuario");
    } finally { setSaving(false); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast.warn("Mínimo 6 caracteres"); return; }
    try {
      await API.post(`/users/${resetModal.id}/reset-password`, { newPassword });
      toast.success("Contraseña actualizada");
      setResetModal(null);
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al cambiar contraseña");
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-gray-500">{users.length} usuario{users.length !== 1 ? "s" : ""} asignado{users.length !== 1 ? "s" : ""}</p>
        <button onClick={() => setCreateModal(true)} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600`}>
          <Plus size={14} /> Nuevo usuario
        </button>
      </div>

      {users.length === 0 ? (
        <Empty icon={Users} label="No hay usuarios asignados a esta empresa" />
      ) : (
        <div className="space-y-2">
          {users.map((u) => {
            const meta = ROLE_META[u.role] ?? ROLE_META.user;
            return (
              <motion.div
                key={u.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100"
              >
                <div className="w-9 h-9 bg-supernova-100 rounded-full flex items-center justify-center shrink-0">
                  <User size={15} className="text-supernova-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{u.name}</p>
                  <p className="text-xs text-gray-500 truncate">{u.email}</p>
                </div>
                <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-semibold border ${meta.cls}`}>
                  {meta.label}
                </span>
                <button
                  onClick={() => setResetModal(u)}
                  title="Cambiar contraseña"
                  className="shrink-0 p-1.5 rounded-lg bg-white border border-gray-200 hover:border-red-300 text-gray-400 hover:text-red-500 transition"
                >
                  <Shield size={13} />
                </button>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Create user modal */}
      <Modal open={createModal} onClose={() => setCreateModal(false)} title="Crear usuario para la empresa">
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Nombre completo *</label>
            <input className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Juan García" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Correo electrónico *</label>
            <input className={inp} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="juan@empresa.com" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Contraseña *</label>
            <input className={inp} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mínimo 6 caracteres" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Rol y permisos</label>
            <select className={inp} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="user">Empleado — acceso básico</option>
              <option value="company">Admin empresa — acceso completo al panel</option>
            </select>
            <p className="text-xs text-gray-400 mt-1.5">
              {form.role === "company"
                ? "Podrá gestionar productos, pedidos, sedes y usuarios de esta empresa."
                : "Acceso de lectura y atención de pedidos."}
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setCreateModal(false)} className={`${btn} border border-gray-200 text-gray-600 hover:bg-gray-50`}>Cancelar</button>
            <button type="submit" disabled={saving} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 disabled:opacity-60`}>
              {saving ? "Creando..." : "Crear usuario"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Reset password modal */}
      <Modal open={!!resetModal} onClose={() => { setResetModal(null); setNewPassword(""); }} title="Cambiar contraseña">
        <p className="text-sm text-gray-600 mb-1">Usuario: <strong className="text-gray-800">{resetModal?.name}</strong></p>
        <p className="text-xs text-gray-400 mb-4">{resetModal?.email}</p>
        <div className="mb-4">
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block uppercase tracking-wider">Nueva contraseña</label>
          <input
            className={inp}
            type="password"
            placeholder="Mínimo 6 caracteres"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleResetPassword(); }}
          />
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={() => { setResetModal(null); setNewPassword(""); }} className={`${btn} border border-gray-200 text-gray-600 hover:bg-gray-50`}>Cancelar</button>
          <button onClick={handleResetPassword} className={`${btn} bg-red-500 text-white hover:bg-red-600`}>
            <Shield size={14} /> Actualizar contraseña
          </button>
        </div>
      </Modal>
    </>
  );
}

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────────
const TABS = [
  { id: "info",      label: "Información", icon: Building2 },
  { id: "sedes",     label: "Sedes",       icon: MapPin },
  { id: "productos", label: "Productos",   icon: Package },
  { id: "secciones", label: "Secciones",   icon: Tags },
  { id: "usuarios",  label: "Usuarios",    icon: Users },
];

export default function AdminCompanyDetail({ company: initialCompany, onBack }) {
  const [company, setCompany] = useState(initialCompany);
  const [categories, setCategories] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [products, setProducts] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("info");
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [resCompany, resCats, resSedes, resProds, resSecs, resUsers] = await Promise.allSettled([
      API.get(`/companies/by-id/${initialCompany.id}`),
      API.get("/categories"),
      API.get(`/sedes/company/${initialCompany.id}`),
      API.get(`/products/company/${initialCompany.id}`),
      API.get(`/secciones/company/${initialCompany.id}`),
      API.get(`/companies/${initialCompany.id}/users`),
    ]);

    if (resCompany.status === "fulfilled") {
      setCompany(resCompany.value.data?.data?.company ?? resCompany.value.data?.data ?? initialCompany);
    }
    if (resCats.status === "fulfilled") {
      setCategories(resCats.value.data?.data?.categories ?? resCats.value.data?.data ?? []);
    }
    if (resSedes.status === "fulfilled") {
      setSedes(resSedes.value.data?.data?.sedes ?? []);
    }
    if (resProds.status === "fulfilled") {
      setProducts(resProds.value.data?.data?.products ?? resProds.value.data?.data ?? []);
    }
    if (resSecs.status === "fulfilled") {
      setSecciones(resSecs.value.data?.data?.secciones ?? []);
    }
    if (resUsers.status === "fulfilled") {
      setUsers(resUsers.value.data?.data?.users ?? resUsers.value.data?.data?.data ?? []);
    }
    setLoading(false);
  }, [initialCompany.id]); // eslint-disable-line

  useEffect(() => { loadAll(); }, [loadAll]);

  const tabCounts = { info: null, sedes: sedes.length, productos: products.length, secciones: secciones.length, usuarios: users.length };
  const logoSrc = getImageUrl(company?.logo);

  return (
    <div className="space-y-5">
      {/* Company header card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200 shrink-0">
            {logoSrc
              ? <img src={logoSrc} alt="Logo" className="w-full h-full object-cover" />
              : <Building2 size={24} className="text-gray-400" />
            }
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <h2 className="font-black text-xl text-gray-800 leading-tight">{company.name}</h2>
                <p className="text-sm text-gray-400 mt-0.5">@{company.slug}</p>
              </div>
              <button
                onClick={onBack}
                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition shrink-0"
              >
                <ArrowLeft size={12} /> Volver a empresas
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2.5">
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${company.status === "Activo"
                ? "bg-green-50 text-green-700 border-green-200"
                : "bg-gray-100 text-gray-500 border-gray-200"}`}>
                {company.status || "Sin estado"}
              </span>
              {company.verified && (
                <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1">
                  <BadgeCheck size={11} /> Verificada
                </span>
              )}
              {company.category && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                  {company.category.name}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-4 gap-2 mt-5 pt-4 border-t border-gray-50">
          {[
            { label: "Sedes", value: sedes.length, icon: MapPin },
            { label: "Productos", value: products.length, icon: Package },
            { label: "Secciones", value: secciones.length, icon: Tags },
            { label: "Usuarios", value: users.length, icon: Users },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="text-center py-1">
              <p className="text-2xl font-black text-gray-800">{loading ? "—" : value}</p>
              <p className="text-xs text-gray-400 flex items-center justify-center gap-1 mt-0.5">
                <Icon size={10} /> {label}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Tab panel */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Tab nav */}
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => {
            const count = tabCounts[id];
            const isActive = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold whitespace-nowrap transition border-b-2 -mb-px
                  ${isActive
                    ? "border-supernova-500 text-supernova-600 bg-supernova-50/40"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50/60"}`}
              >
                <Icon size={15} />
                {label}
                {count !== null && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold min-w-[1.25rem] text-center ${isActive ? "bg-supernova-500 text-white" : "bg-gray-100 text-gray-500"}`}>
                    {loading ? "·" : count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="p-5 sm:p-6">
          {loading ? (
            <div className="space-y-3 animate-pulse">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-100 rounded-xl" style={{ opacity: 1 - i * 0.2 }} />
              ))}
            </div>
          ) : (
            <>
              {activeTab === "info"      && <InfoTab company={company} categories={categories} onRefresh={loadAll} />}
              {activeTab === "sedes"     && <SedesTab company={company} sedes={sedes} setSedes={setSedes} />}
              {activeTab === "productos" && <ProductosTab company={company} products={products} setProducts={setProducts} secciones={secciones} sedes={sedes} />}
              {activeTab === "secciones" && <SeccionesTab company={company} secciones={secciones} setSecciones={setSecciones} />}
              {activeTab === "usuarios"  && <UsuariosTab company={company} users={users} setUsers={setUsers} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
